import { useState } from 'react';
import { ArrowLineDown, ClockCounterClockwise, Lock, PiggyBank, Target } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';
import { formatPotMonthsRemaining } from '../../utils/potMonthsRemaining';
import { MoneyDisplay } from '../common/MoneyDisplay';
import { ProgressBar } from '../common/ProgressBar';
import { useExpenseSheet } from '../../features/expenses/SheetProvider';
import { ClosePotDialog } from './ClosePotDialog';
import { PotHistoryDialog } from './PotHistoryDialog';

function toneFor(pct) {
  if (pct >= 100) return 'success';
  if (pct >= 50) return 'sage';
  if (pct >= 25) return 'warning';
  return 'danger';
}

function potIcon(kind) {
  return kind === 'occasional' ? PiggyBank : Target;
}

function PotMetricsInline({ remaining, monthlyRequired, monthsLabel }) {
  const items = [
    {
      label: pt.pots.progress.remaining,
      value: formatEUR(remaining),
      valueClass: 'text-foreground',
    },
    {
      label: pt.pots.progress.monthlyRequired,
      value: formatEUR(monthlyRequired),
      valueClass: 'text-foreground',
    },
    {
      label: pt.pots.progress.monthsRemaining,
      value: monthsLabel,
      valueClass: 'text-sage-800',
    },
  ];

  return (
    <p className="text-xs leading-snug text-muted-foreground">
      {items.map((item, index) => (
        <span key={item.label}>
          {index > 0 ? <span className="text-stone-300"> · </span> : null}
          <span className="text-muted-foreground">{item.label} </span>
          <span
            className={cn('font-semibold tabular-nums', item.valueClass)}
            title={typeof item.value === 'string' ? item.value : undefined}
          >
            {item.value}
          </span>
        </span>
      ))}
    </p>
  );
}

function PotActionLink({ icon: Icon, label, onClick, primary = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium transition-colors',
        primary
          ? 'text-sage-700 hover:text-sage-900'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      <span>{label}</span>
    </button>
  );
}

function PotFooterActions({ isClosed, onDeposit, onHistory, onClose }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 border-t border-border pt-2 sm:pt-2.5">
      {!isClosed ? (
        <PotActionLink
          icon={ArrowLineDown}
          label={pt.pots.contribute.submit}
          onClick={onDeposit}
          primary
        />
      ) : null}
      <PotActionLink
        icon={ClockCounterClockwise}
        label={pt.pots.history.title}
        onClick={onHistory}
      />
      {!isClosed ? (
        <PotActionLink
          icon={Lock}
          label={pt.pots.close.cta}
          onClick={onClose}
        />
      ) : null}
    </div>
  );
}

function PotRowBody({
  pot,
  accent,
  Icon,
  pct,
  accumulated,
  target,
  remaining,
  monthlyRequired,
  monthsLabel,
  isClosed,
  onDeposit,
  onHistory,
  onClose,
  dialogs,
  className,
  variant,
}) {
  const isCard = variant === 'card';

  return (
    <li
      className={cn(
        'box-border w-full min-w-0 list-none',
        isCard && 'rounded-xl border border-border/90 bg-card p-3 shadow-sm sm:p-4',
        !isCard && 'px-3 py-3 sm:px-4 sm:py-4',
        isClosed && 'opacity-80',
        className,
      )}
    >
      <div className="flex gap-3">
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${accent}22` }}
        >
          <Icon className="h-4 w-4" style={{ color: accent }} aria-hidden />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:gap-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3
                  className="truncate font-display text-[15px] font-semibold leading-snug text-foreground"
                  title={pot.name}
                >
                  {pot.name}
                </h3>
                {isClosed ? (
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {pt.pots.closedBadge}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <p className="font-numeric text-base font-semibold leading-none text-foreground tnum">
                {Math.round(pct)}%
              </p>
              <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                <MoneyDisplay
                  value={accumulated}
                  size="sm"
                  className="inline font-medium text-foreground"
                />
                <span className="text-muted-foreground/70"> / {formatEUR(target)}</span>
              </p>
            </div>
          </div>

          <ProgressBar value={pct} tone={toneFor(pct)} className="h-1.5" />

          <PotMetricsInline
            remaining={remaining}
            monthlyRequired={monthlyRequired}
            monthsLabel={monthsLabel}
          />

          <PotFooterActions
            isClosed={isClosed}
            onDeposit={onDeposit}
            onHistory={onHistory}
            onClose={onClose}
          />
        </div>
      </div>

      {dialogs}
    </li>
  );
}

/**
 * @param {{ pot: object, className?: string, variant?: 'list' | 'card' }} props
 */
export function PotRow({ pot, className, variant = 'list' }) {
  const [showClose, setShowClose] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { open: openExpenseSheet } = useExpenseSheet();

  const pct = Math.min(100, Math.max(0, Number(pot.progress_pct || 0)));
  const accumulated = Number(pot.accumulated || 0);
  const target = Number(pot.target_amount || 0);
  const remaining = Number(pot.remaining || Math.max(target - accumulated, 0));
  const monthlyRequired = Number(pot.monthly_required || 0);
  const monthsLabel = formatPotMonthsRemaining(
    pot.months_remaining,
    pt.pots.progress,
  );
  const isClosed = Boolean(pot.is_closed);
  const accent = pot.color || '#B58A4F';
  const Icon = potIcon(pot.kind);

  const handleDeposit = () => {
    openExpenseSheet(null, { savingsPotId: pot.id });
  };

  const dialogs = (
    <>
      <PotHistoryDialog
        pot={pot}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
      <ClosePotDialog
        pot={pot}
        isOpen={showClose}
        onClose={() => setShowClose(false)}
      />
    </>
  );

  return (
    <PotRowBody
      pot={pot}
      accent={accent}
      Icon={Icon}
      pct={pct}
      accumulated={accumulated}
      target={target}
      remaining={remaining}
      monthlyRequired={monthlyRequired}
      monthsLabel={monthsLabel}
      isClosed={isClosed}
      onDeposit={handleDeposit}
      onHistory={() => setShowHistory(true)}
      onClose={() => setShowClose(true)}
      dialogs={dialogs}
      className={className}
      variant={variant}
    />
  );
}
